// ProjectsViewContainer.js
import React, { Component } from 'react';
import { styled } from '@mui/material/styles';
import Axios from 'axios';
import { API_URL } from '../../utils/config/config';

import Header from '../../components/post-security/common/header';
import Sidebar from '../../components/post-security/common/sidebar';
import Projects from '../../components/post-security/project_view/projects';
import ProjectDetailedViewContainer from './ProjectDetailedViewContainer';
import { DEFAULT_SHOW_COLUMNS } from '../../components/post-security/project_view/constants';
import ProjectSidebar from '../../components/post-security/project_view/projectSidebar';
import Footer from '../../components/post-security/common/footer';
import BouncingPreloader from '../../components/post-security/common/bouncyLoader';
import { SFDC_STAGE_OPTIONS, SFDC_DIVISIONS, SFDC_COMPANIES } from '../../components/post-security/common/constants';

import {
  Typography, Button, Snackbar, Alert, Box,
  FormControl, InputLabel, MenuItem, Select, TextField,
} from '@mui/material';
import { Replay } from '@mui/icons-material';

const PREFIX = 'ProjectsViewContainer';
const classes = {
  root_grid: `${PREFIX}-root_grid`,
  main: `${PREFIX}-main`,
  header: `${PREFIX}-header`,
  footer: `${PREFIX}-footer`
};

const Root = styled('div')(({ theme }) => ({
  [`& .${classes.root_grid}`]: {
    display: 'grid',
    maxHeight: '100vh',
    gridTemplateRows: '4rem calc(100vh - 6rem) 2rem',
  },
  [`& .${classes.main}`]: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    padding: '20px',
    paddingLeft: '70px',
    overflow: 'hidden',
    maxHeight: 'calc(100vh - 4rem)',
    width: '100%',
    boxSizing: 'border-box',
    gap: '16px',
    alignItems: 'start',
  },
  [`& .${classes.header}`]: { zIndex: 999999 },
  [`& .${classes.footer}`]: { maxHeight: '2rem' }
}));

const DEFAULT_PAGE_SIZE = 50;
const MAX_PAGE_SIZE = 100;

class ProjectsViewContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      pmResourceNames: [{ assignment_resource: 0, full_name: 'Loading....' }],
      project_view_data: [],
      filtered_project_view_data: [],
      loading: true,
      error: false,
      offline: false,
      filters: { ops_leaders: [], divisions: [], companies: [], stages: [] },
      profile_data: [],
      snackbar_show: false,
      snackbar_message: '',
      snackbar_type: 'info',
      divisions: [],
      ops_leaders: [],
      stages: [],
      companies: [],
      showColumns: DEFAULT_SHOW_COLUMNS,
      showDetailedView: false,
      detail_view_project_info: null,
      sidebarOpen: false,

      // pagination
      page: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      count: 0,
      nextUrl: null,
      prevUrl: null,
      fetchError: null,

      // search
      searchQuery: '',
    };

    // bindings
    this.toggleDetailView = this.toggleDetailView.bind(this);
    this.updateDetailView = this.updateDetailView.bind(this);
    this.updateFilters = this.updateFilters.bind(this);
    this.applyFilters = this.applyFilters.bind(this);
    this.updateShowColumns = this.updateShowColumns.bind(this);
    this.updateJobData = this.updateJobData.bind(this);
    this.fetchPage = this.fetchPage.bind(this);
    this.handlePrev = this.handlePrev.bind(this);
    this.handleNext = this.handleNext.bind(this);
    this.handlePageSizeChange = this.handlePageSizeChange.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.handleSidebarToggle = this.handleSidebarToggle.bind(this);

    // ---- DEV double-mount guard (StrictMode) ----
    this._didInitOnce = false;

    // track cancel tokens to abort on unmount/page change
    this._cancels = [];
  }

  // --------- UI helpers ----------
  toggleDetailView(boo) {
    this.setState({
      showDetailedView: boo,
      // Only clear detail_view_project_info when closing the view
      detail_view_project_info: boo ? this.state.detail_view_project_info : null
    });
  }
  updateDetailView(values) { this.setState({ detail_view_project_info: values }); }
  updateShowColumns(new_values) { this.setState({ showColumns: new_values }); }

  // --------- Filters ----------
    async updateFilters(filter_type, new_values) {
        // update local state (still save user prefs)
        const obj = {};
        obj[filter_type] = new_values;
        const new_filters = { ...this.state.filters, ...obj };
        this.setState({ filters: new_filters });

        // best-effort: persist user's filter prefs (non-blocking)
        try {
            const authHeaders = await this.createAuthHeaders();
            const data = { user_id: localStorage.getItem('id'), filters: new_filters };
            Axios.patch(`${API_URL}project_view_filters/${localStorage.getItem('id')}/`, data, authHeaders);
        } catch {}

        // 👉 server-side filtering: reset to page 1 and fetch with filters
        this._cancels.forEach(c => c.cancel('filters-change'));
        this._cancels = [];
        this.fetchPage(1, this.state.pageSize, this.state.searchQuery, new_filters);
    }

  applyFilters(filters, sourceData = null) {
    const t0 = performance.now();

    const dataToFilter = sourceData || this.state.project_view_data;
    // If there are NO active filters, don't do any work – just mirror the page data
    const hasFilters =
      (filters.ops_leaders?.length || 0) +
      (filters.divisions?.length || 0) +
      (filters.companies?.length || 0) +
      (filters.stages?.length || 0) > 0;

    if (!hasFilters) {
      this.setState({ filtered_project_view_data: dataToFilter });
      return;
    }

    let filtered_data = [...dataToFilter];

    for (const [key, value] of Object.entries(filters)) {
      if (Array.isArray(value) && value.length > 0) {
        let key_db = '';
        switch (key) {
          case 'ops_leaders': key_db = 'sfdc_ops_leader'; break;
          case 'divisions': key_db = ['sfdc_division', 'sfdc_second_fa_division', 'sfdc_third_fa_division', 'sfdc_fourth_fa_division']; break;
          case 'companies': key_db = ['sfdc_company', 'sfdc_second_fa_company', 'sfdc_third_fa_company', 'sfdc_fourth_fa_company']; break;
          case 'stages': key_db = 'sfdc_stage'; break;
          default: continue;
        }
        if (Array.isArray(key_db)) {
          filtered_data = filtered_data.filter(el => key_db.some(field => value.includes(el[field])));
        } else {
          filtered_data = filtered_data.filter(el => value.includes(el[key_db]));
        }
      }
    }

    this.setState({ filtered_project_view_data: filtered_data });
  }

  // --------- Auth / Axios ----------
  async createAuthHeaders() {
    const { getAccessTokenSilently } = this.props.auth;
    const token = await getAccessTokenSilently();
    return {
      withCredentials: true,
      headers: {
        'content-type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      // 15s hard timeout so the UI doesn't hang forever
      timeout: 15000,
    };
  }

  _withCancel(config) {
    const source = Axios.CancelToken.source();
    this._cancels.push(source);
    return { ...config, cancelToken: source.token };
  }

  componentWillUnmount() {
    // cancel any in-flight requests when leaving the page
    this._cancels.forEach(c => c.cancel('unmount'));
    this._cancels = [];
  }

  // --------- Data helpers ----------
  extractOpsLeaders(projectData) {
    return [...new Set(projectData.map(project => project.sfdc_ops_leader))]
      .filter(Boolean)
      .sort()
      .map(leader => ({ title: leader }));
  }

  updateDetailedViewInfo(projectData) {
    if (this.state.detail_view_project_info) {
      const updatedDetailInfo = projectData.find(
        project => project.id === this.state.detail_view_project_info.id
      );
      return updatedDetailInfo ? {
        detail_view_project_info: updatedDetailInfo,
        snackbar_show: true,
        snackbar_message: 'Save Successful!',
        snackbar_type: 'success',
      } : {};
    }
    return {};
  }

  // --------- PAGINATED FETCH (DRF) ----------
  async fetchPage(
    page = this.state.page,
    pageSize = this.state.pageSize,
    searchQuery = this.state.searchQuery,
    filters = this.state.filters
) {
  const pageT0 = performance.now();
  this.setState({ loading: true, fetchError: null });

  try {
    const authHeaders = await this.createAuthHeaders();

    // Build query string (supports multi-value filters)
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('page_size', String(pageSize));

    if (searchQuery && searchQuery.trim()) {
      params.set('search', searchQuery.trim());
    }

    const appendMulti = (key, arr) => {
      (arr || []).forEach(v => { if (v) params.append(key, v); });
    };
    appendMulti('ops_leaders', filters.ops_leaders);
    appendMulti('divisions',   filters.divisions);
    appendMulti('companies',   filters.companies);
    appendMulti('stages',      filters.stages);

    const url = `${API_URL}project_view/?${params.toString()}`;

    console.time('[Projects][fetchPage] network');
    const response = await Axios.get(url, this._withCancel(authHeaders));
    console.timeEnd('[Projects][fetchPage] network');

    const parseT0 = performance.now();
    const { count, next, previous, results } = response.data ?? {};
    const projectData = Array.isArray(results) ? results : [];
    const parseT1 = performance.now();

    const opsT0 = performance.now();
    const detailedViewUpdates = this.updateDetailedViewInfo(projectData);
    const extractedOpsLeaders = this.extractOpsLeaders(projectData);
    const opsT1 = performance.now();

    const setStateT0 = performance.now();
    this.setState({
      loading: false,
      error: false,
      project_view_data: projectData,
      filtered_project_view_data: projectData, // server already filtered
      ops_leaders: extractedOpsLeaders,
      divisions: SFDC_DIVISIONS,
      companies: SFDC_COMPANIES,
      stages: SFDC_STAGE_OPTIONS,
      count: count ?? 0,
      nextUrl: next || null,
      prevUrl: previous || null,
      page,
      pageSize,
      searchQuery,
      ...detailedViewUpdates,
    }, () => {
      const setStateT1 = performance.now();
      // keep applyFilters as a no-op (server has filtered already)
      this.applyFilters(this.state.filters, projectData);

      const pageT1 = performance.now();
      const ms = t => `${t.toFixed(0)}ms`;
      console.log(
        `[Projects][PAGE PERF] page=${page} size=${pageSize} ` +
        `parse=${ms(parseT1 - parseT0)} ops=${ms(opsT1 - opsT0)} ` +
        `setState=${ms(setStateT1 - setStateT0)} total=${ms(pageT1 - pageT0)}`
      );
    });
  } catch (error) {
    if (!Axios.isCancel(error)) {
      console.error('[ProjectsViewContainer] fetchPage error:', error);
      this.setState({
        error: true,
        loading: false,
        fetchError: error?.message || 'Failed to load project data',
        snackbar_show: true,
        snackbar_message: 'Failed to load project data',
        snackbar_type: 'error',
      });
    }
  }
}

  handlePrev() {
    if (!this.state.prevUrl || this.state.page <= 1) return;
    // cancel current and fetch previous
    this._cancels.forEach(c => c.cancel('page-change'));
    this._cancels = [];
    this.fetchPage(this.state.page - 1, this.state.pageSize, this.state.searchQuery);
  }

  handleNext() {
    const totalPages = Math.max(1, Math.ceil(this.state.count / this.state.pageSize));
    if (!this.state.nextUrl || this.state.page >= totalPages) return;
    this._cancels.forEach(c => c.cancel('page-change'));
    this._cancels = [];
    this.fetchPage(this.state.page + 1, this.state.pageSize, this.state.searchQuery);
  }

  handlePageSizeChange(e) {
    const val = Number(e.target.value);
    const clamped = Math.min(Math.max(1, val), MAX_PAGE_SIZE);
    
    // Prevent multiple rapid calls
    if (this.state.loading) return;
    
    // Cancel any pending requests
    this._cancels.forEach(c => c.cancel('page-size-change'));
    this._cancels = [];
    
    // Update page size in state immediately for UI responsiveness
    this.setState({ 
      pageSize: clamped, 
      page: 1, // Reset to page 1 when changing page size
      loading: true 
    }, () => {
      // Fetch with new page size after state is updated
      this.fetchPage(1, clamped, this.state.searchQuery);
    });
  }

  handleSearchChange(searchQuery) {
    // Update search state and trigger server-side search
    this.setState({ searchQuery });
    // Cancel any pending requests
    this._cancels.forEach(c => c.cancel('search-change'));
    this._cancels = [];
    // Reset to page 1 when searching and fetch with new search query
    this.fetchPage(1, this.state.pageSize, searchQuery);
  }

  handleSidebarToggle() {
    this.setState({sidebarOpen: !this.state.sidebarOpen});
  }

  // --------- Updates ---------
  async updateJobData(job_id, data) {
    const authHeaders = await this.createAuthHeaders();
    this.setState({ snackbar_show: true, snackbar_message: 'Saving...', snackbar_type: 'info' });

    Axios.patch(`${API_URL}projects/update/${job_id}/`, data, this._withCancel(authHeaders))
      .then((response) => {
        if (response.status === 200) {
          this.setState({
            snackbar_show: true,
            snackbar_message: 'Change Saved Successfully',
            snackbar_type: 'success'
          });

          const idx = this.state.project_view_data.findIndex(({ id }) => id === response.data.id);
          if (idx !== -1) {
            const updatedProjectData = [...this.state.project_view_data];
            updatedProjectData[idx] = { ...updatedProjectData[idx], ...response.data };
            this.setState({ project_view_data: updatedProjectData }, () => this.applyFilters(this.state.filters));
          }
        }
      })
      .catch(error => {
        if (!Axios.isCancel(error)) {
          this.setState({
            snackbar_show: true,
            snackbar_message: 'Error Saving Change',
            snackbar_type: 'error'
          });
        }
      });
  }

  // --------- Boot ---------
  async componentDidMount() {
    // Guard: StrictMode in React 18 will mount -> unmount -> mount again in DEV
    if (this._didInitOnce) return;
    this._didInitOnce = true;

    document.title = 'Project View';
    this.fetchPage(1, this.state.pageSize);
    this.fetchPMResourceNames();
    this.fetchFilterData();
  }

  async fetchPMResourceNames() {
    try {
      const authHeaders = await this.createAuthHeaders();
      const response = await Axios.get(`${API_URL}pm_resources_names/`, this._withCancel(authHeaders));
      const sortedResourceNames = response.data
        .filter(resource => resource.full_name)
        .sort((a, b) => a.full_name.localeCompare(b.full_name));
      this.setState({ pmResourceNames: sortedResourceNames });
    } catch (error) {
      if (!Axios.isCancel(error)) {
        this.setState({
          snackbar_show: true,
          snackbar_message: 'Failed to load PM resource names',
          snackbar_type: 'error',
        });
      }
    }
  }

  async fetchFilterData() {
    const userId = localStorage.getItem('id');
    if (!userId) return;
    try {
      const authHeaders = await this.createAuthHeaders();

      const filterResponse = await Axios.get(`${API_URL}project_view_filters/${userId}/`, this._withCancel(authHeaders));
      if (filterResponse.status === 200) {
        const filters = { ...filterResponse.data.filters };
        delete filters.column_options;
        this.setState({ filters }, () => {
          if (!this.state.loading && Array.isArray(this.state.project_view_data)) {
            this.applyFilters(this.state.filters);
          }
        });
      }

      const profileResponse = await Axios.get(`${API_URL}profile/`, this._withCancel(authHeaders));
      if (profileResponse.status === 200) {
        this.setState({ profile_data: profileResponse.data });
      }
    } catch (error) {
      if (!Axios.isCancel(error)) {
        this.setState({ error: true });
      }
    }
  }



  render() {
    const totalPages = Math.max(1, Math.ceil(this.state.count / this.state.pageSize));
    const sidebarWidth = this.state.sidebarOpen ? 400 : 70;

    return (
      <Root>
        <div className={classes.root_grid}>
          <div className={classes.header}>
            <Header />
          </div>

          <div>
            <Sidebar />

            {this.state.offline ? (
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                You are offline!
              </div>
            ) : this.state.error ? (
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                <Typography variant="h5" sx={{ fontFamily: 'inherit' }} gutterBottom>
                  <strong>An error occurred!</strong>
                </Typography>
                <center>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => window.location.reload()}
                    startIcon={<Replay />}
                  >
                    Try again
                  </Button>
                </center>
              </div>
            ) : (
              <>
                <Snackbar
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  open={this.state.snackbar_show}
                  autoHideDuration={15000}
                  onClose={() => this.setState({ snackbar_show: false })}
                >
                  <Alert elevation={6} variant="filled" severity={this.state.snackbar_type} sx={{ width: '100%' }}>
                    {this.state.snackbar_message}
                  </Alert>
                </Snackbar>

                <div
                  className={classes.main}
                  /* reserve space for the persistent right sidebar */
                  style={{ paddingRight: sidebarWidth + 16 }}
                >
                  {/* Main content area: Projects table + pager + detailed view */}
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 1, 
                    minWidth: 0,
                    width: '100%',
                    height: 'calc(100vh - 120px)', // Full viewport minus header space
                    overflow: 'hidden'
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 2, 
                      flex: 1, 
                      minHeight: 0,
                      overflow: 'hidden'
                    }}>
                      {/* Projects Table and Pagination Container */}
                      <Box sx={{ 
                        flex: this.state.showDetailedView ? '0 1 60%' : '1', 
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%'
                      }}>
                        {!this.state.loading ? (
                          <>
                            <Projects
                              // Data props
                              projectViewData={this.state.filtered_project_view_data}
                              pmResourceNames={this.state.pmResourceNames}
                              filters={this.state.filters}

                              // State props
                              loading={this.state.loading}
                              showColumns={this.state.showColumns}
                              showDetailedView={this.state.showDetailedView}
                              detailViewProjectInfo={this.state.detail_view_project_info}
                              searchQuery={this.state.searchQuery}

                              // Function props
                              updateJobData={this.updateJobData}
                              updateDetailView={this.updateDetailView}
                              toggleDetailView={this.toggleDetailView}
                              onSearchChange={this.handleSearchChange}
                            />

                            {/* Pager Controls */}
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: 1,
                                flexWrap: 'wrap',
                                borderTop: '1px solid #e5e7eb',
                                backgroundColor: '#f8f9fa',
                                background: '#fff',
                                position: 'sticky',
                                bottom: 0,
                                zIndex: 2,
                              }}
                            >
                              {/* Left spacer to center the main controls */}
                              <Box sx={{ flex: 1 }} />
                              
                              {/* Centered pagination controls */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={this.handlePrev}
                                  disabled={!this.state.prevUrl || this.state.loading || this.state.page <= 1}
                                >
                                  Prev
                                </Button>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <span>Page {this.state.page} of {totalPages}</span>
                                </Box>

                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={this.handleNext}
                                  disabled={!this.state.nextUrl || this.state.loading || this.state.page >= totalPages}
                                >
                                  Next
                                </Button>

                                <FormControl size="small" sx={{ minWidth: 100, ml: 1 }}>
                                  <InputLabel id="page-size-label">Rows</InputLabel>
                                  <Select
                                    labelId="page-size-label"
                                    label="Rows"
                                    value={this.state.pageSize}
                                    onChange={this.handlePageSizeChange}
                                  >
                                    {[25, 50, 100].map(sz => (
                                      <MenuItem key={sz} value={sz}>{sz}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Box>

                              {/* Right-aligned total count */}
                              <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                                <Box sx={{ opacity: 0.8, fontSize: '0.875rem' }}>
                                  {this.state.loading ? 'Loading…' : `Total: ${this.state.count.toLocaleString()}`}
                                </Box>
                              </Box>
                            </Box>
                          </>
                        ) : (
                          <Box sx={{ 
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: 'calc(100vh - 200px)',
                            minHeight: '400px',
                            flex: 1,
                            position: 'relative'
                          }}>
                            <BouncingPreloader />
                          </Box>
                        )}
                      </Box>

                      {/* Detailed View */}
                      {this.state.showDetailedView ? (
                        <Box sx={{ 
                          flex: '0 1 40%', 
                          minWidth: '350px',
                          display: 'flex',
                          flexDirection: 'column'
                        }}>
                          <ProjectDetailedViewContainer
                            loading={this.state.loading}
                            detail_view_project_info={this.state.detail_view_project_info}
                            toggleDetailView={this.toggleDetailView}
                            pmResourceNames={this.state.pmResourceNames}
                            fetchData={() => this.fetchPage(this.state.page, this.state.pageSize, this.state.searchQuery)}
                            multiply_by_sfdc_probability={this.state.filters.multiply_by_sfdc_probability}
                            profile_data={this.state.profile_data.find(e => e.id === this.state.detail_view_project_info?.profile_type)}
                            auth={this.props.auth}
                          />
                        </Box>
                      ) : null}
                    </Box>

                  </Box>

                </div>

                {/* Right Sidebar */}
                <ProjectSidebar
                  open={this.state.sidebarOpen}
                  onToggle={this.handleSidebarToggle}
                  loading={this.state.loading}
                  filters={this.state.filters}
                  divisions={this.state.divisions}
                  ops_leaders={this.state.ops_leaders}
                  stages={this.state.stages}
                  companies={this.state.companies}
                  updateFilters={this.updateFilters}
                  showColumns={this.state.showColumns}
                  // FIX: pass the bound method, not a state property
                  updateShowColumns={this.updateShowColumns}
                />
              </>
            )}
          </div>

          <div>
            <Footer className={classes.footer} />
          </div>
        </div>
      </Root>
    );
  }
}

export default ProjectsViewContainer;
